import { useState, useEffect, useCallback } from "react";
import { useAuth } from "./useAuth";
import { supabase } from "../lib/supabase";
import type { UserRole } from "@jumpbean/shared";

/**
 * Admin authentication state
 */
export interface AdminAuthState {
  /** Whether the current user has admin role */
  isAdmin: boolean;
  /** Whether we're still loading the admin status */
  isLoading: boolean;
  /** The user's role (user or admin) */
  role: UserRole | null;
  /** Error message if admin check failed */
  error: string | null;
  /** Refresh the admin status */
  refreshAdminStatus: () => Promise<void>;
}

/**
 * Hook to check if the current user has admin privileges.
 *
 * This hook:
 * - Fetches the user's role from the profiles table
 * - Caches the result until auth state changes
 * - Automatically refreshes when user logs in/out
 *
 * @example
 * ```tsx
 * function AdminDashboard() {
 *   const { isAdmin, isLoading, error } = useAdminAuth();
 *
 *   if (isLoading) return <Spinner />;
 *   if (!isAdmin) return <Redirect to="/" />;
 *
 *   return <AdminContent />;
 * }
 * ```
 */
export function useAdminAuth(): AdminAuthState {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch the user's role from the database
   */
  const fetchRole = useCallback(async () => {
    if (!user || !supabase) {
      setIsAdmin(false);
      setRole(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", user.id)
        .single();

      if (fetchError) {
        console.error("Failed to fetch user role:", fetchError);
        setError("Failed to check admin status");
        setIsAdmin(false);
        setRole(null);
      } else {
        // Type assertion needed because supabase client types may not include role column yet
        const profileData = data as { role?: string } | null;
        const userRole = (profileData?.role as UserRole) || "user";
        setRole(userRole);
        setIsAdmin(userRole === "admin");
      }
    } catch (err) {
      console.error("Error checking admin status:", err);
      setError("Failed to check admin status");
      setIsAdmin(false);
      setRole(null);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Fetch role when user changes
  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    fetchRole();
  }, [user?.id, isAuthLoading, fetchRole]);

  // Refresh function for manual refresh
  const refreshAdminStatus = useCallback(async () => {
    await fetchRole();
  }, [fetchRole]);

  return {
    isAdmin,
    isLoading: isAuthLoading || isLoading,
    role,
    error,
    refreshAdminStatus,
  };
}

export default useAdminAuth;
